/* ============================================================================
   MW STUFFS — Premium Cart Drawer V2
   Replaces cart-drawer.js when V2 is enabled (loaded conditionally).
   ----------------------------------------------------------------------------
   Architecture:
   - Defines <cart-drawer> custom element (same tag as v1) so product-form,
     Shiprocket, cart.js, etc. find it via document.querySelector('cart-drawer')
     without modification.
   - Section ID stays 'cart-drawer'. Inner panel ID stays '#CartDrawer'.
   - renderContents() bakes in the documented blank-after-AJAX-add fix:
       fetch /cart?section_id=cart-drawer with cache: 'no-store',
       replace innerHTML of outer <cart-drawer>, sync className,
       force is-empty removal, add active/animate/open, lock body.
   - Survives soft-loads: hooks the cart icon on every refresh.
   ============================================================================ */

(function () {
  'use strict';

  if (customElements.get('cart-drawer')) {
    console.warn('[CartDrawer V2] <cart-drawer> already defined — V2 will not load. Disable the original cart-drawer.js when V2 is on.');
    return;
  }

  const SECTION_ID = 'cart-drawer';
  const BODY_OPEN_CLASS = 'cd-v2-open';

  /* ---------- Helpers ---------- */
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const safeFetch = (url, opts = {}) =>
    fetch(url, Object.assign({ cache: 'no-store', credentials: 'same-origin' }, opts));

  /* ============================================================
     <cart-drawer>  — V2
     ============================================================ */
  class CartDrawerV2 extends HTMLElement {
    constructor() {
      super();
      this._bound = false;
      this._activeElement = null;
      this._isOpening = false;
    }

    connectedCallback() {
      // Move to body to avoid stacking-context traps (per documented fix)
      if (this.parentElement && this.parentElement !== document.body) {
        document.body.appendChild(this);
      }

      this._bindCartIcon();
      this._bindInternal();
      this._subscribeCartUpdate();
      this._bindEscape();

      // Set up Shiprocket node management. Shiprocket binds its BUY NOW button
      // ONCE on page load to the .shiprocket-headless div. We never destroy that
      // node afterwards — we move it in/out of view as cart state changes.
      this._setupShiprocketManager();

      // Handle back-forward cache (bfcache) restores. When the user taps the
      // browser BACK button (e.g. returning from Shiprocket checkout), the page
      // is restored from a frozen snapshot. That snapshot's cart-drawer markup
      // and cart-icon bubble can be STALE — showing an empty cart even though
      // items exist. We detect the bfcache restore via pageshow.persisted and
      // silently reconcile the cart state + icon bubble so the next open() shows
      // the correct items.
      if (!this._pageshowBound) {
        this._pageshowBound = true;
        window.addEventListener('pageshow', (e) => {
          if (e.persisted) {
            // Restored from bfcache — refresh cart bubble + drawer contents
            // without opening the drawer.
            this._refreshCartBubble();
            this._reconcileCartSilently();
          }
        });
      }
    }

    /* ---------- Silent cart reconcile (no open) ----------
       Fetches the live cart section and swaps the drawer's inner markup WITHOUT
       opening/animating it. Used after a bfcache restore so a later open() shows
       fresh contents. ---------- */
    async _reconcileCartSilently() {
      try {
        const res = await fetch(`/?section_id=${SECTION_ID}&_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetched = doc.querySelector('cart-drawer.cd-v2');
        if (!fetched) return;

        // Don't clobber an open drawer mid-interaction
        if (this.classList.contains('active')) return;

        this._rescueShiprocket();
        this.innerHTML = fetched.innerHTML;
        this.className = fetched.className;
        const cartHasItems = !!this.querySelector('.cd-v2__item');
        if (cartHasItems) this.classList.remove('is-empty');
        this._placeShiprocket();
        this._bindInternal();
      } catch (e) { /* silent */ }
    }

    /* ---------- Shiprocket button lifecycle manager ----------
       The problem: Shiprocket injects its button only on full page load and
       never re-scans the DOM. Our cart refresh uses innerHTML replacement which
       destroys DOM nodes. So we must (a) let Shiprocket bind at page load, then
       (b) preserve that exact node by MOVING it (never recreating it).

       Strategy:
       - A persistent, off-screen holder lives in document.body. It is never
         touched by cart-drawer innerHTML replacement, so a node parked there
         survives any number of cart refreshes.
       - We watch for Shiprocket to finish injecting the button, then take
         ownership of the live .shiprocket-headless node.
       - _placeShiprocket() puts the live node into the footer mount when the
         cart has items, or parks it in the holder when empty.
       - Before any innerHTML replace, _rescueShiprocket() parks the live node
         in the holder so the replace can't destroy it. After the replace,
         _placeShiprocket() puts it back if needed.
    ------------------------------------------------------------ */
    _setupShiprocketManager() {
      // Create the persistent off-screen holder once
      if (!CartDrawerV2._srHolder) {
        const holder = document.createElement('div');
        holder.id = 'cd-v2-sr-holder';
        // Off-screen but with layout (1x1) so Shiprocket never falls back to a
        // fixed floating button due to a zero-size mount.
        holder.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;';
        document.body.appendChild(holder);
        CartDrawerV2._srHolder = holder;
      }

      // Find the live .shiprocket-headless node anywhere it currently lives
      // (in the drawer footer mount, or already parked in the holder).
      const findNode = () =>
        this.querySelector('.shiprocket-headless') ||
        CartDrawerV2._srHolder.querySelector('.shiprocket-headless');

      // A node counts as "the live bound button" once Shiprocket has injected
      // child content into it.
      const tryCapture = () => {
        const node = findNode();
        if (node && node.children.length > 0) {
          CartDrawerV2._srNode = node;
          this._placeShiprocket();
          return true;
        }
        return false;
      };

      // Capture immediately if already injected
      if (tryCapture()) return;

      // Otherwise poll + observe until Shiprocket injects. Shiprocket binds only
      // once on page load, but the timing varies (esp. on slow connections), so
      // we watch for up to ~12s.
      let attempts = 0;
      const maxAttempts = 48; // ~12s at 250ms
      const poll = setInterval(() => {
        attempts++;
        if (tryCapture() || attempts >= maxAttempts) {
          clearInterval(poll);
          if (CartDrawerV2._srObserver) {
            CartDrawerV2._srObserver.disconnect();
            CartDrawerV2._srObserver = null;
          }
        }
      }, 250);

      // Observe the entire drawer subtree — Shiprocket may inject into the mount
      // OR replace the .shiprocket-headless node entirely; subtree:true catches both.
      const mo = new MutationObserver(() => {
        if (tryCapture()) {
          mo.disconnect();
          CartDrawerV2._srObserver = null;
          clearInterval(poll);
        }
      });
      mo.observe(this, { childList: true, subtree: true });
      CartDrawerV2._srObserver = mo;

      // Fire Meta Pixel InitiateCheckout when the Shiprocket BUY NOW button is
      // clicked. We do this OURSELVES instead of relying on Shiprocket, because
      // moving the button node around the DOM (above) detaches Shiprocket's own
      // InitiateCheckout tracking. This listener is purely additive — it changes
      // nothing about how the drawer or the button behaves; it only emits the
      // pixel event so the Meta ad campaign gets its checkout signal back.
      this._bindInitiateCheckout();
    }

    /* ---------- Meta Pixel: InitiateCheckout on BUY NOW ----------
       Attaches ONE capture-phase click listener on the drawer. When the click
       lands inside the Shiprocket button (.shiprocket-headless), we fetch the
       live cart and fire fbq('track','InitiateCheckout', …) with value, currency,
       num_items and content_ids — mirroring what the native cart drawer sends.
       Capture phase + a short de-dupe window guarantee it fires exactly once and
       BEFORE the redirect to Shiprocket checkout begins. ---------- */
    _bindInitiateCheckout() {
      if (this._icBound) return;   // bind once per element
      this._icBound = true;

      document.addEventListener('click', (e) => {
        // Only react to clicks on/inside a Shiprocket BUY NOW button
        const sr = e.target.closest && e.target.closest('.shiprocket-headless');
        if (!sr) return;

        // De-dupe: avoid double-fire from bubbling / rapid taps (1.2s window)
        const now = Date.now();
        if (CartDrawerV2._lastICFire && (now - CartDrawerV2._lastICFire) < 1200) return;
        CartDrawerV2._lastICFire = now;

        this._fireInitiateCheckout();
      }, true); // capture phase — runs before Shiprocket's redirect
    }

    _fireInitiateCheckout() {
      // Pixel must be present; if not, nothing to do.
      if (typeof window.fbq !== 'function') return;

      // Pull the live cart so the event payload is accurate even if the drawer
      // markup is mid-refresh. Falls back to a minimal event if the fetch fails.
      safeFetch('/cart.js')
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          let payload;
          if (cart && Array.isArray(cart.items)) {
            payload = {
              value: (cart.total_price || 0) / 100, // paise → rupees
              currency: (cart.currency || 'INR'),
              num_items: cart.item_count || cart.items.length,
              content_ids: cart.items.map((it) => String(it.product_id)),
              content_type: 'product',
            };
          } else {
            payload = { currency: 'INR' };
          }
          try { window.fbq('track', 'InitiateCheckout', payload); } catch (err) {}
        })
        .catch(() => {
          // Fire a minimal event rather than miss the signal entirely
          try { window.fbq('track', 'InitiateCheckout', { currency: 'INR' }); } catch (err) {}
        });
    }

    // Move the live Shiprocket node into the right place based on cart state.
    _placeShiprocket() {
      const node = CartDrawerV2._srNode;
      if (!node) return;

      const cartHasItems = !!this.querySelector('.cd-v2__item');
      const footer = this.querySelector('[data-cd-footer]');
      const mount = this.querySelector('[data-cd-sr-mount]');

      if (cartHasItems && mount) {
        // Put the live button into the footer mount, replacing any empty placeholder
        const placeholder = mount.querySelector('.shiprocket-headless');
        if (placeholder && placeholder !== node) {
          placeholder.replaceWith(node);
        } else if (!mount.contains(node)) {
          mount.appendChild(node);
        }
        if (footer) footer.classList.remove('cd-v2__footer--empty');
      } else {
        // Cart empty — park the live node off-screen and hide the footer
        if (CartDrawerV2._srHolder && !CartDrawerV2._srHolder.contains(node)) {
          CartDrawerV2._srHolder.appendChild(node);
        }
        if (footer) footer.classList.add('cd-v2__footer--empty');
      }
    }

    // Rescue the live Shiprocket node out of the drawer before innerHTML replace
    // so the replace can't destroy it.
    _rescueShiprocket() {
      const node = CartDrawerV2._srNode;
      if (node && CartDrawerV2._srHolder && !CartDrawerV2._srHolder.contains(node)) {
        CartDrawerV2._srHolder.appendChild(node);
      }
    }

    /* ---------- Cart icon hook (header trigger) ---------- */
    _bindCartIcon() {
      const links = $$('#cart-icon-bubble, a[href$="/cart"], a[href*="/cart?"]');
      links.forEach((link) => {
        if (link.dataset.cdV2Bound) return;
        link.dataset.cdV2Bound = '1';
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
        link.addEventListener('click', (e) => {
          // Allow ctrl/cmd-click to open cart page
          if (e.metaKey || e.ctrlKey || e.shiftKey) return;
          e.preventDefault();
          this.open(link);
        });
      });
    }

    _subscribeCartUpdate() {
      // Dawn pub/sub support — refreshes V2 when OTHER components change the cart
      // (e.g. PDP add-to-cart form, third-party apps).
      //
      // CRITICAL: filter out self-published events. Without this filter, _refreshSection
      // publishes cartUpdate → our own subscriber catches it → calls _refreshSection
      // → publishes again → infinite flicker loop.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Ignore events we published ourselves
          if (event && event.source === 'cart-drawer-v2') return;
          // Also ignore if we're currently mid-refresh — prevents races with other publishers
          if (this._isRefreshing) return;
          // Soft refresh on external updates (do not auto-open)
          this._refreshSection(false);
        });
      }
    }

    _bindEscape() {
      this.addEventListener('keyup', (e) => {
        if (e.code === 'Escape') this.close();
      });
    }

    /* ---------- Open / Close ---------- */
    open(triggeredBy) {
      if (triggeredBy) this._activeElement = triggeredBy;

      // Lock body, hide WA widget
      document.body.classList.add(BODY_OPEN_CLASS, 'overflow-hidden');

      // Animate open immediately so the drawer feels responsive
      requestAnimationFrame(() => {
        this.classList.add('active', 'animate');
        this.setAttribute('open', '');
      });

      // Place Shiprocket button for whatever state is currently shown
      this._placeShiprocket();

      // CRITICAL: the server-rendered cart-drawer markup can be STALE when the
      // drawer is opened — e.g. after returning from Shiprocket checkout via the
      // browser back button, the page is restored from bfcache with an outdated
      // (often empty) cart-drawer, even though the real cart has items and the
      // cart-icon bubble shows the right count. Trusting that stale HTML makes
      // the drawer show "Your Cart (0)" when items actually exist.
      //
      // Fix: always re-fetch the live cart section on open and reconcile the
      // drawer contents. _refreshSection keeps it open (shouldOpen=true) and
      // swaps in the fresh markup. This is cheap and guarantees correctness.
      this._refreshSection(true);

      // Move focus to close button when transition ends
      this.addEventListener('transitionend', () => {
        const focusTarget = this.querySelector('[data-cd-close]') || this.querySelector('.cd-v2__panel');
        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      }, { once: true });
    }

    close() {
      this.classList.remove('active', 'animate');
      this.removeAttribute('open');
      document.body.classList.remove(BODY_OPEN_CLASS, 'overflow-hidden');

      // Restore focus
      if (this._activeElement && typeof this._activeElement.focus === 'function') {
        try { this._activeElement.focus(); } catch (e) {}
      }
    }

    /* ---------- Compat: product-form expects these ---------- */
    setActiveElement(el) {
      this._activeElement = el;
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: 'cart-drawer' },
        { id: 'cart-icon-bubble' }
      ];
    }

    /**
     * Called by product-form.js after add-to-cart.
     * Implements the documented blank-after-AJAX-add fix:
     * skip parsedState, fetch fresh section, replace outer innerHTML,
     * sync class, remove is-empty, manually open.
     */
    renderContents(parsedState) {
      this._isOpening = true;
      this._refreshSection(true).finally(() => {
        this._isOpening = false;
      });
    }

    /* ---------- Section refresh (the AJAX-add fix) ---------- */
    async _refreshSection(shouldOpen) {
      // Cancel any in-flight refresh — last action wins
      if (this._refreshAbort) {
        try { this._refreshAbort.abort(); } catch (e) {}
      }
      this._refreshAbort = new AbortController();
      const signal = this._refreshAbort.signal;

      // Guard: any pub/sub event published DURING this refresh is ignored by our
      // own subscriber, breaking potential feedback loops (Shiprocket events,
      // Dawn cartUpdate, third-party listeners that re-publish).
      this._isRefreshing = true;

      try {
        const sectionUrl = `/cart?section_id=${SECTION_ID}&_=${Date.now()}`;
        const bubbleUrl = `/?section_id=cart-icon-bubble&_=${Date.now()}`;

        // Fire both fetches in PARALLEL (was sequential before — half the wait)
        const [sectionRes, bubbleRes] = await Promise.all([
          fetch(sectionUrl, { signal, cache: 'no-store', headers: { 'Accept': 'text/html' } }),
          fetch(bubbleUrl, { signal, cache: 'no-store', headers: { 'Accept': 'text/html' } })
        ]);

        if (!sectionRes.ok) throw new Error(`Section fetch ${sectionRes.status}`);
        const html = await sectionRes.text();

        // Parse and validate that V2 markup came back (not V1)
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetched = doc.querySelector('cart-drawer.cd-v2');
        if (!fetched) {
          console.error(
            '[CartDrawer V2] Section refetch returned non-V2 markup. ' +
            'Fix: wrap sections/cart-drawer.liquid with the V2 conditional. ' +
            'See INTEGRATION-INSTRUCTIONS.md Step 3.'
          );
          return false;
        }

        // ---- PRESERVE SCROLL POSITION ACROSS innerHTML REPLACE ----
        const oldBody = this.querySelector('.cd-v2__body');
        const prevScrollTop = oldBody ? oldBody.scrollTop : 0;

        // ---- RESCUE SHIPROCKET BUTTON before innerHTML replace ----
        // Move the live Shiprocket node to the off-screen holder so the
        // upcoming innerHTML replace cannot destroy it.
        this._rescueShiprocket();

        // Preserve open state
        const wasActive = this.classList.contains('active');
        const wasOpen = this.hasAttribute('open');

        // Replace innerHTML of the OUTER cart-drawer element
        this.innerHTML = fetched.innerHTML;

        // Sync className from fetched response (correct is-empty state)
        this.className = fetched.className;

        // Force is-empty removal if cart now has items
        const cartHasItems = !!this.querySelector('.cd-v2__item');
        if (cartHasItems) this.classList.remove('is-empty');

        // Restore active state
        if (shouldOpen || wasActive || wasOpen) {
          this.classList.add('active', 'animate');
          this.setAttribute('open', '');
          document.body.classList.add(BODY_OPEN_CLASS, 'overflow-hidden');
        }

        // ---- PLACE SHIPROCKET BUTTON after replace ----
        // Put the preserved live node back into the footer (if items) or leave
        // it parked (if empty). Never recreated, so the binding survives.
        this._placeShiprocket();

        // Restore scroll position after layout settles
        requestAnimationFrame(() => {
          const newBody = this.querySelector('.cd-v2__body');
          if (newBody && prevScrollTop > 0) {
            // Clamp to new body's scrollable area in case content shrunk
            newBody.scrollTop = Math.min(prevScrollTop, newBody.scrollHeight - newBody.clientHeight);
          }
        });

        // Re-bind everything inside (qty buttons, remove, FBT, etc.)
        this._bindInternal();

        // Handle the bubble response we already fetched in parallel
        if (bubbleRes && bubbleRes.ok) {
          this._applyCartBubble(await bubbleRes.text());
        }

        // Pop the unlock toast if a new offer just activated
        this._maybeShowUnlockToast();

        // Notify other components (Dawn pub/sub)
        this._publishCartUpdate();

        return true;
      } catch (err) {
        if (err.name === 'AbortError') return false;  // expected when superseded
        console.error('[CartDrawer V2] refresh failed:', err);
        return false;
      } finally {
        // Keep the guard up briefly after refresh ends so any *asynchronously*
        // re-emitted cartUpdate events from listeners (Shiprocket, etc.) still
        // get filtered out and don't trigger another refresh.
        setTimeout(() => { this._isRefreshing = false; }, 400);
      }
    }

    /* ---------- Apply pre-fetched cart bubble HTML ---------- */
    _applyCartBubble(html) {
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetched = doc.querySelector('#cart-icon-bubble');
        const live = document.querySelector('#cart-icon-bubble');
        if (fetched && live) {
          live.innerHTML = fetched.innerHTML;
          delete live.dataset.cdV2Bound;
          this._bindCartIcon();
        }
      } catch (e) { /* silent */ }
    }

    async _refreshCartBubble() {
      // Standalone bubble refresh — used when section fetch is skipped/fails.
      // (Normal flow now uses _applyCartBubble via the parallel fetch in _refreshSection.)
      try {
        const res = await fetch(`/?section_id=cart-icon-bubble&_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        this._applyCartBubble(await res.text());
      } catch (e) { /* silent */ }
    }

    _publishCartUpdate() {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-v2' }); } catch (e) {}
      }
    }

    /* ---------- Bind internal interactions ---------- */
    _bindInternal() {
      // Overlay & close button
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && !overlay.dataset.cdBound) {
        overlay.dataset.cdBound = '1';
        overlay.addEventListener('click', () => this.close());
      }
      const closeBtn = this.querySelector('[data-cd-close]');
      if (closeBtn && !closeBtn.dataset.cdBound) {
        closeBtn.dataset.cdBound = '1';
        closeBtn.addEventListener('click', () => this.close());
      }

      // Qty buttons
      $$('[data-cd-qty]', this).forEach((btn) => {
        if (btn.dataset.cdBound) return;
        btn.dataset.cdBound = '1';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const line = parseInt(btn.dataset.line, 10);
          const action = btn.dataset.cdQty;
          this._handleQtyStep(line, action);
        });
      });

      // Qty inputs (debounced manual edit)
      $$('.cd-v2__qty-input', this).forEach((input) => {
        if (input.dataset.cdBound) return;
        input.dataset.cdBound = '1';
        let t;
        input.addEventListener('input', (e) => {
          clearTimeout(t);
          t = setTimeout(() => {
            const line = parseInt(input.dataset.line, 10);
            const q = Math.max(0, parseInt(input.value, 10) || 0);
            this._updateLine(line, q);
          }, 700);
        });
      });

      // Remove buttons
      $$('[data-cd-remove]', this).forEach((btn) => {
        if (btn.dataset.cdBound) return;
        btn.dataset.cdBound = '1';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const line = parseInt(btn.dataset.line, 10);
          this._updateLine(line, 0);
        });
      });

      // FBT quick add
      $$('[data-cd-quick-add]', this).forEach((btn) => {
        if (btn.dataset.cdBound) return;
        btn.dataset.cdBound = '1';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          if (!variantId) return;
          this._quickAdd(btn, variantId);
        });
      });
    }

    /* ---------- Qty step (+ / -) ---------- */
    _handleQtyStep(line, action) {
      // Ignore clicks while a previous update for this line is still in flight.
      // Prevents the "hung" feel where rapid taps queue up multiple round-trips.
      const itemEl = this.querySelector(`.cd-v2__item[data-line="${line}"]`);
      if (itemEl && itemEl.classList.contains('is-loading')) return;

      const input = this.querySelector(`.cd-v2__qty-input[data-line="${line}"]`);
      if (!input) return;
      let q = parseInt(input.value, 10) || 0;
      if (action === 'increase') q += 1;
      else if (action === 'decrease') q = Math.max(0, q - 1);

      // Instant visual echo — number updates before network completes
      input.value = q;

      this._updateLine(line, q);
    }

    /* ---------- Update line ---------- */
    async _updateLine(line, quantity) {
      const itemEl = this.querySelector(`.cd-v2__item[data-line="${line}"]`);
      if (itemEl) itemEl.classList.add('is-loading');

      // Guard pub/sub during this update too — same as _refreshSection
      this._isRefreshing = true;

      try {
        // SINGLE-ROUNDTRIP: ask Shopify to return rendered section HTML
        // in the same response as the cart change. Cuts wait time roughly in half
        // on slow connections (was 2 sequential network calls).
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            line: line,
            quantity: quantity,
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });

        if (!res.ok) throw new Error(`Cart change ${res.status}`);
        const data = await res.json();
        this._applySectionsFromResponse(data && data.sections);
      } catch (err) {
        console.error('[CartDrawer V2] line update failed:', err);
        if (itemEl) itemEl.classList.remove('is-loading');
      } finally {
        setTimeout(() => { this._isRefreshing = false; }, 400);
      }
    }

    /* ---------- Apply cart-drawer + cart-icon-bubble sections returned by
       /cart/change.js or /cart/add.js with the `sections` parameter.
       Mirrors _refreshSection's DOM swap + scroll/Shiprocket preservation,
       but without a second network round-trip. ---------- */
    _applySectionsFromResponse(sections) {
      if (!sections) return;

      const drawerHtml = sections['cart-drawer'];
      const bubbleHtml = sections['cart-icon-bubble'];

      if (drawerHtml) {
        const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
        // /cart/change.js returns the section's INNER HTML (the rendered
        // template content), not wrapped in a <cart-drawer> element. So we
        // pull out the cart-drawer.cd-v2 if present, else use the whole body.
        const fetched = doc.querySelector('cart-drawer.cd-v2');

        // Preserve scroll
        const oldBody = this.querySelector('.cd-v2__body');
        const prevScrollTop = oldBody ? oldBody.scrollTop : 0;

        // Rescue Shiprocket button before innerHTML replace
        this._rescueShiprocket();

        const wasActive = this.classList.contains('active');
        const wasOpen = this.hasAttribute('open');

        if (fetched) {
          this.innerHTML = fetched.innerHTML;
          this.className = fetched.className;
        } else {
          // Section returned without the wrapping <cart-drawer> element —
          // use the body innerHTML which IS the rendered section content.
          this.innerHTML = doc.body.innerHTML;
        }

        const cartHasItems = !!this.querySelector('.cd-v2__item');
        if (cartHasItems) this.classList.remove('is-empty');

        if (wasActive || wasOpen) {
          this.classList.add('active', 'animate');
          this.setAttribute('open', '');
          document.body.classList.add(BODY_OPEN_CLASS, 'overflow-hidden');
        }

        // Place the preserved Shiprocket button (footer if items, parked if empty)
        this._placeShiprocket();

        // Restore scroll
        requestAnimationFrame(() => {
          const newBody = this.querySelector('.cd-v2__body');
          if (newBody && prevScrollTop > 0) {
            newBody.scrollTop = Math.min(prevScrollTop, newBody.scrollHeight - newBody.clientHeight);
          }
        });

        this._bindInternal();
        this._maybeShowUnlockToast();
        this._publishCartUpdate();
      }

      if (bubbleHtml) {
        this._applyCartBubble(bubbleHtml);
      }
    }

    /* ---------- FBT quick add ---------- */
    async _quickAdd(btn, variantId) {
      btn.classList.add('is-loading');
      const originalText = btn.textContent;
      btn.textContent = 'Adding…';
      this._isRefreshing = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1 }],
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });

        if (!res.ok) {
          const errJson = await res.json().catch(() => ({}));
          throw new Error(errJson.description || `Add failed (${res.status})`);
        }

        const data = await res.json();
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.textContent = '✓ Added';
        setTimeout(() => {
          this._applySectionsFromResponse(data && data.sections);
        }, 350);
      } catch (err) {
        console.error('[CartDrawer V2] quick add failed:', err);
        btn.classList.remove('is-loading');
        btn.textContent = originalText;
        // Brief error indication
        btn.style.borderColor = '#D63333';
        btn.style.color = '#D63333';
        setTimeout(() => {
          btn.style.borderColor = '';
          btn.style.color = '';
        }, 1500);
        this._isRefreshing = false;
      } finally {
        setTimeout(() => { this._isRefreshing = false; }, 800);
      }
    }

    /* ---------- Unlock toast (when a new tier activates) ---------- */
    _maybeShowUnlockToast() {
      const unlockedNow = $$('.cd-v2__offer.is-unlocked', this).map(
        (el) => el.querySelector('.cd-v2__offer-text strong')?.textContent?.trim() || ''
      );

      const prev = JSON.parse(sessionStorage.getItem('cdV2Unlocked') || '[]');
      const fresh = unlockedNow.filter((x) => x && !prev.includes(x));

      if (fresh.length > 0) {
        this._popToast(`🎉 ${fresh[0]}`);
      }
      sessionStorage.setItem('cdV2Unlocked', JSON.stringify(unlockedNow));
    }

    _popToast(text) {
      const panel = this.querySelector('.cd-v2__panel');
      if (!panel) return;
      let toast = panel.querySelector('.cd-v2__toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cd-v2__toast';
        panel.appendChild(toast);
      }
      toast.textContent = text;
      requestAnimationFrame(() => toast.classList.add('is-shown'));
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => toast.classList.remove('is-shown'), 2800);
    }
  }

  customElements.define('cart-drawer', CartDrawerV2);

  /* ============================================================
     <cart-drawer-items>  — V2
     ============================================================
     Required because Dawn's CartItems superclass calls
     getSectionsToRender() to know what to update after qty changes.
     V2 routes everything through cart-drawer's _refreshSection(),
     so this class just delegates to its parent <cart-drawer>.
     ============================================================ */
  class CartDrawerItemsV2 extends HTMLElement {
    constructor() {
      super();
    }

    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: 'cart-drawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
    }
  }

  // Only define if Dawn's CartItems hasn't already
  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', CartDrawerItemsV2);
  }

})();